import TwilioVoiceClient from 'dashboard/api/channel/voice/twilioVoiceClient';
import WebCallClient from 'dashboard/api/channel/voice/webCallClient';
import { cleanupWhatsappSession } from 'dashboard/composables/useWhatsappCallSession';
import { VOICE_CALL_PROVIDERS } from 'dashboard/helper/inbox';
import { TERMINAL_STATUSES } from 'dashboard/helper/voice';
import { VOICE_CALL_DIRECTION } from 'dashboard/components-next/message/constants';
import { defineStore } from 'pinia';

const teardownByProvider = call => {
  if (call?.provider === VOICE_CALL_PROVIDERS.WHATSAPP) {
    cleanupWhatsappSession();
  } else if (call?.provider === VOICE_CALL_PROVIDERS.WEB) {
    // Web calls run inside a Cloudflare RealtimeKit room — leaving it here
    // (not just clearing local state) is what stops the agent's mic from
    // staying hot in the room once the call drops out of the store.
    WebCallClient.disconnect();
  } else {
    TwilioVoiceClient.endClientCall();
  }
};

export const useCallsStore = defineStore('calls', {
  state: () => ({
    calls: [],
    transferOffers: [],
  }),

  getters: {
    activeCall: state => state.calls.find(call => call.isActive) || null,
    hasActiveCall: state => state.calls.some(call => call.isActive),
    incomingCalls: state => state.calls.filter(call => !call.isActive),
    hasIncomingCall: state => state.calls.some(call => !call.isActive),
    hasTransferOffer: state => state.transferOffers.length > 0,
    isActiveCallOnHold: state =>
      Boolean(state.calls.find(call => call.isActive)?.onHold),
  },

  actions: {
    handleCallStatusChanged({ callSid, status }) {
      if (!TERMINAL_STATUSES.includes(status)) return;

      const call = this.calls.find(c => c.callSid === callSid);
      // WhatsApp recordings live in the in-memory recorder until voice_call.ended
      // uploads them; tearing down here would race-wipe those chunks.
      if (call?.provider === 'whatsapp') {
        this.calls = this.calls.filter(c => c.callSid !== callSid);
        return;
      }

      this.removeCall(callSid);
    },

    addCall(callData) {
      if (!callData?.callSid) return;
      const existing = this.calls.find(c => c.callSid === callData.callSid);
      if (existing) {
        // Merge so a later cable event with sdp_offer/provider/caller fills in
        // gaps left by the earlier message.created path (and vice versa).
        // Preserve a previously-captured caller snapshot when the incoming
        // event has no sender info, otherwise the widget would flip to
        // "Unknown caller" on the next status update.
        const next = { ...callData };
        if (existing.caller && !next.caller) delete next.caller;
        // Same reseed as a fresh call below (design 14.4), but fall back to
        // the already-stored value first so a payload with no hold info
        // (e.g. a fresh outbound-call addCall from a different call site)
        // never clobbers hold state set via setHoldState/the cable broadcast.
        next.onHold = callData.onHold ?? existing.onHold ?? false;
        next.holdSeconds = callData.holdSeconds ?? existing.holdSeconds ?? 0;
        // Same reseed for the originator's pending transfer (design 14.4) —
        // fall back to the already-stored value first so a payload with no
        // transfer info never clobbers a transfer set via setCallTransfer/the
        // cable broadcast.
        next.transfer = callData.transfer ?? existing.transfer ?? null;
        // Same reseed for the two-agent roster (design 14.4, spec R12): a
        // payload with no roster must never clobber one set by
        // setConnectedAgents or the cable broadcast.
        next.connectedAgents =
          callData.connectedAgents ?? existing.connectedAgents ?? [];
        Object.assign(existing, next, { isActive: existing.isActive });
        return;
      }

      // Prepend so the newest call surfaces as the primary card (incomingCalls[0])
      // and older calls drop into the stack below it.
      this.calls.unshift({
        ...callData,
        // Reseed hold state from whatever the caller passed in (e.g. the
        // message.call payload's on_hold/hold_seconds — design 14.4), or
        // default it for callers that never carry hold info (a brand-new
        // outbound call always starts off-hold).
        onHold: callData.onHold ?? false,
        holdSeconds: callData.holdSeconds ?? 0,
        // Same reseed for a pending transfer (design 14.4); a brand-new call
        // never starts with one.
        transfer: callData.transfer ?? null,
        connectedAgents: callData.connectedAgents ?? [],
        isActive: false,
      });
    },

    removeCall(callSid) {
      const callToRemove = this.calls.find(c => c.callSid === callSid);
      // Outbound web calls join the RealtimeKit room at create time but stay
      // non-active until the customer answers — tear the room down even for a
      // non-active entry, or a no-answer/decline leaves the agent's mic hot.
      const isOutboundWebRing =
        callToRemove?.provider === VOICE_CALL_PROVIDERS.WEB &&
        callToRemove?.callDirection === VOICE_CALL_DIRECTION.OUTBOUND;
      if (callToRemove?.isActive || isOutboundWebRing) {
        teardownByProvider(callToRemove);
      }
      this.calls = this.calls.filter(c => c.callSid !== callSid);
    },

    setCallActive(callSid) {
      this.calls = this.calls.map(call => ({
        ...call,
        isActive: call.callSid === callSid,
      }));
    },

    // Hold state is only ever written from a server response or the
    // voice_call.hold_changed broadcast — never optimistically (design 12.1).
    // `holdSeconds` is the server's running total. Omit it to change only the
    // flag; passing 0 is what starts a fresh hold window.
    setHoldState({ callSid, onHold, holdSeconds }) {
      const call = this.calls.find(c => c.callSid === callSid);
      if (!call) return;
      call.onHold = Boolean(onHold);
      call.holdSeconds = holdSeconds ?? call.holdSeconds ?? 0;
    },

    // An offer addressed to this agent. Keyed by transfer id so a repeat
    // broadcast (reconnect, retry) replaces rather than stacks.
    addTransferOffer(offer) {
      if (!offer?.transfer?.id) return;
      const index = this.transferOffers.findIndex(
        item => item.transfer.id === offer.transfer.id
      );
      if (index === -1) this.transferOffers.push(offer);
      else this.transferOffers.splice(index, 1, offer);
    },

    removeTransferOffer(transferId) {
      this.transferOffers = this.transferOffers.filter(
        item => item.transfer.id !== transferId
      );
    },

    setCallTransfer({ callSid, transfer }) {
      const call = this.calls.find(c => c.callSid === callSid);
      if (!call) return;
      call.transfer = transfer;
    },

    // Written only from a server response or a cable broadcast, never
    // optimistically — same rule as hold state.
    setConnectedAgents({ callSid, connectedAgents }) {
      const call = this.calls.find(c => c.callSid === callSid);
      if (!call) return;
      call.connectedAgents = connectedAgents || [];
    },

    clearActiveCall() {
      const active = this.calls.find(c => c.isActive);
      teardownByProvider(active);
      this.calls = this.calls.filter(call => !call.isActive);
    },

    dismissCall(callSid) {
      this.calls = this.calls.filter(call => call.callSid !== callSid);
    },

    removeCallsForConversation(conversationId) {
      const callsToRemove = this.calls.filter(
        call => call.conversationId === conversationId
      );

      // Tear down each active call via its own provider so a WhatsApp call
      // gets cleanupWhatsappSession() (closes pc, stops recorder/mic) instead
      // of the Twilio-only endClientCall() — otherwise mic stays open.
      callsToRemove.filter(call => call.isActive).forEach(teardownByProvider);

      this.calls = this.calls.filter(
        call => call.conversationId !== conversationId
      );
    },
  },
});
